import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { ActiveAlertsContainer } from '@/experiences/sales-portal/domain/insights/containers/active-alerts-container';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { ContentWithPanelTemplate } from '@experiences/templates/content-with-panel-template';
import { createStyles } from '@/contexts/theme-context';
import { HiddenAlertsContainer } from '@/experiences/sales-portal/domain/insights/containers/hidden-alerts-container';
import { HubCardType } from '@/types';
import { ItemDetailDrawer } from '@/experiences/sales-portal/domain/items/containers/item-detail-drawer';
import { ItemDetailItemInfo } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { LowestPerformingCustomersCard } from '@/experiences/sales-portal/domain/insights/components/lowest-performing-customers-card';
import { LowestPerformingCustomersCardSkeleton } from '@/experiences/sales-portal/domain/insights/components/lowest-performing-customers-card-skeleton';
import { ProfitRollupCard } from '@/experiences/sales-portal/domain/insights/components/profit-rollup-card';
import { ProfitRollupCardSkeleton } from '@/experiences/sales-portal/domain/insights/components/profit-rollup-card-skeleton';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { ScrollView } from 'react-native';
import { useAccountDetailPress } from '@/experiences/sales-portal/domain/insights/blocs/hooks/use-account-detail-press';
import { useActiveAlertsLoader } from '@/experiences/sales-portal/domain/insights/blocs/loaders/use-active-alerts-loader';
import { useAuth } from '@/contexts/auth-context';
import { useHiddenAlertsLoader } from '@/experiences/sales-portal/domain/insights/blocs/loaders/use-hidden-alerts-loader';
import {
  useInsightNavigation,
  useSelectedLostItemAlertUUID,
} from '@/experiences/sales-portal/domain/insights/blocs/hooks/use-insight-navigation';
import { useLayoutContext } from '@/contexts/layout-context';
import { useLowestPerformingCustomersLoader } from '@/experiences/sales-portal/domain/insights/blocs/loaders/use-lowest-performing-customers-loader';
import { useProfitRollupLoader } from '@/experiences/sales-portal/domain/insights/blocs/loaders/use-profit-rollup-loader';
import { useSalesPortalSupplierMetadataLoader } from '@/experiences/sales-portal/blocs/loaders/use-sales-portal-supplier-metadata-loader';
import { useSalesRepConfig } from '@/experiences/sales-portal/blocs/hooks/use-sales-rep-config';
import { XStack, YStack } from '@usepepper/sprout-kit-ui';
import type { LostItemAlert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';

interface Props {
  supplierUUID: string;
}

export const InsightsScreen: React.FC<Props> = ({ supplierUUID }) => {
  const openAccountDetail = useAccountDetailPress(supplierUUID);
  const [styles, theme] = useStyles();
  const { isSmallTabletLayout } = useLayoutContext();

  const authenticatedUserUUID = useAuth((s) => s.authenticatedUserUUID);
  const { close } = useInsightNavigation();
  const selectedLostItemAlertUUID = useSelectedLostItemAlertUUID();

  const { timezone } = useSalesPortalSupplierMetadataLoader({ supplierUUID });

  const { salesRepConfig, loading: salesRepConfigLoading } =
    useSalesRepConfig(supplierUUID);
  const isGrossProfitDisabled = salesRepConfig
    ? salesRepConfig.disable_gross_profit_display === true
    : true;

  // Load both alert feeds at the screen level so they fetch in parallel and the
  // containers stay presentational. The hidden section also gates on the active
  // loading flag so it never pops in above a still-loading active feed.
  const {
    alerts: activeAlerts,
    loading: activeAlertsLoading,
    error: activeAlertsError,
    refetch: refetchActiveAlerts,
  } = useActiveAlertsLoader();
  const {
    alerts: hiddenAlerts,
    loading: hiddenAlertsLoading,
    error: hiddenAlertsError,
  } = useHiddenAlertsLoader();

  const selectedLostItemAlert = useMemo<LostItemAlert | null>(
    () =>
      activeAlerts.find(
        (alert): alert is LostItemAlert =>
          alert.cardType === HubCardType.LostItem &&
          alert.alertUUID === selectedLostItemAlertUUID,
      ) ?? null,
    [activeAlerts, selectedLostItemAlertUUID],
  );

  const lostItemInfo = useMemo<ItemDetailItemInfo | null>(
    () =>
      selectedLostItemAlert
        ? {
            description: '',
            externalItemId: selectedLostItemAlert.externalItemId || null,
            imageUrl: selectedLostItemAlert.photoUrls?.[0] ?? null,
            itemUUID: null,
            metadata: null,
            name: selectedLostItemAlert.displayName,
            packDisplayString: selectedLostItemAlert.packUnit,
            upcList: [],
          }
        : null,
    [selectedLostItemAlert],
  );

  // profit rollup insights
  const {
    data: profitRollupHubCardData,
    loading: profitRollupLoading,
    error,
  } = useProfitRollupLoader(authenticatedUserUUID!, isGrossProfitDisabled);

  const isProfitRollupCardLoading =
    salesRepConfigLoading || profitRollupLoading || !authenticatedUserUUID;
  const shouldShowProfitRollupCard = !error && profitRollupHubCardData;

  // lowest performing customers insights
  const {
    data: lowestPerformingCustomersHubCardData,
    loading: lowestPerformingCustomersLoading,
    error: lowestPerformingCustomersError,
  } = useLowestPerformingCustomersLoader(
    authenticatedUserUUID!,
    isGrossProfitDisabled,
  );

  const isLowestPerformingCustomersCardLoading =
    salesRepConfigLoading ||
    lowestPerformingCustomersLoading ||
    !authenticatedUserUUID;
  const shouldShowLowestPerformingCustomersCard =
    !lowestPerformingCustomersError && lowestPerformingCustomersHubCardData;

  const mainColumnFlex = isSmallTabletLayout ? 7 : 8;
  const sideColumnFlex = isSmallTabletLayout ? 5 : 4;

  return (
    <SalesHubShellTemplate>
      <ContentWithPanelTemplate
        onClose={close}
        panelContent={
          selectedLostItemAlert && lostItemInfo ? (
            <ItemDetailDrawer
              itemInfo={lostItemInfo}
              key={selectedLostItemAlert.alertUUID}
              onClose={close}
              restaurantUUID={selectedLostItemAlert.restaurantUUID}
              supplierUUID={supplierUUID}
              timezone={timezone}
              variantPackUUID={selectedLostItemAlert.variantPackUUID}
            />
          ) : undefined
        }
      >
        <AppPageLayout
          mode="focused"
          scroll={false}
          title={i18n.t('insights_hub')}
        >
          <XStack
            flexBasis={0}
            flexGrow={1}
            gap={theme.sk.spacing.spaceMedium}
            paddingTop={theme.sk.spacing.spaceMedium}
          >
            <YStack flexBasis={0} flexGrow={mainColumnFlex}>
              <ScrollView
                contentContainerStyle={styles.alertsScrollContent}
                showsVerticalScrollIndicator={false}
                style={styles.alertsScroll}
              >
                <ActiveAlertsContainer
                  alerts={activeAlerts}
                  error={activeAlertsError}
                  loading={activeAlertsLoading}
                  refetch={refetchActiveAlerts}
                  supplierUUID={supplierUUID}
                />
                <HiddenAlertsContainer
                  activeAlertsLoading={activeAlertsLoading}
                  alerts={hiddenAlerts}
                  error={hiddenAlertsError}
                  loading={hiddenAlertsLoading}
                  supplierUUID={supplierUUID}
                />
              </ScrollView>
            </YStack>
            <YStack
              flexBasis={0}
              flexGrow={sideColumnFlex}
              gap={theme.sk.spacing.spaceSmall}
            >
              {isProfitRollupCardLoading ? (
                <ProfitRollupCardSkeleton />
              ) : shouldShowProfitRollupCard ? (
                <ProfitRollupCard data={profitRollupHubCardData} />
              ) : (
                <></>
              )}
              {isLowestPerformingCustomersCardLoading ? (
                <LowestPerformingCustomersCardSkeleton />
              ) : shouldShowLowestPerformingCustomersCard ? (
                <LowestPerformingCustomersCard
                  data={lowestPerformingCustomersHubCardData}
                  onCustomerPress={openAccountDetail}
                />
              ) : (
                <></>
              )}
            </YStack>
          </XStack>
        </AppPageLayout>
      </ContentWithPanelTemplate>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  alertsScroll: {
    flex: 1,
  },
  alertsScrollContent: {
    gap: theme.sk.spacing.spaceMedium,
    paddingBottom: theme.sk.spacing.spaceSmall,
  },
}));
